import { DateInput, Validator } from "react-admin";
import { useWatch } from "react-hook-form";
import { PolicyStatus } from "@/types/policy";
import { formatDateDisplay, parseDateInput } from "./dateInputHelpers";

/**
 * Builds the termination-date validators for a term (#7320, ADR-081 D5):
 * required when the policy is cancelled, and inside [start, end]. Dates are
 * compared as YYYY-MM-DD strings, which sort lexically.
 */
export const terminationDateValidators = (
  start: string | null | undefined,
  end: string | null | undefined
): Validator[] => [
  (value: unknown) => (value ? undefined : "Termination date is required when the policy is cancelled"),
  (value: unknown) => {
    if (!value || !start) return undefined;
    return String(value).slice(0, 10) >= start.slice(0, 10)
      ? undefined
      : "Termination date must not be before the effective start date";
  },
  (value: unknown) => {
    if (!value || !end) return undefined;
    return String(value).slice(0, 10) <= end.slice(0, 10)
      ? undefined
      : "Termination date must not be after the effective end date";
  },
];

/**
 * Termination date for the policy create / edit form. Renders only while the
 * form's status is "cancelled" — the API requires the date in that case and
 * rejects it in every other (mid-term cancellation is a hard stop for both
 * accumulation windows; the contracted term stays on effective_end_date).
 */
export const TerminationDateInput = ({ source = "termination_date" }: { source?: string }) => {
  const [status, start, end] = useWatch({ name: ["status", "effective_start_date", "effective_end_date"] });
  if (status !== PolicyStatus.CANCELLED) return null;
  return (
    <DateInput
      source={source}
      label="Termination Date"
      helperText="Mid-term cancellation. Nothing incurred or paid after this date counts toward the deductible."
      parse={parseDateInput}
      format={formatDateDisplay}
      validate={terminationDateValidators(start as string | null, end as string | null)}
      isRequired
    />
  );
};

export default TerminationDateInput;
